import {
  SHEET_WIDTH_IN,
  PRICE_PER_SQ_IN,
  ARTWORK_CHECK_FEE,
  UI_PPI,
  EXPORT_DPI,
  DESIGN_GAP_IN,
  GANG_SHEET_PRICES,
} from "pages/dtf-transfers/gang-sheet/data";

export const getSheetUnitPrice = (width = SHEET_WIDTH_IN, height) => {
  const w = Number(width);
  const h = Number(height);
  if (w === 22 && GANG_SHEET_PRICES[h] !== undefined) {
    return GANG_SHEET_PRICES[h];
  }
  return Number((w * h * PRICE_PER_SQ_IN).toFixed(2));
};

export const getArtworkCheckFee = (artworkCheck = false) =>
  artworkCheck ? ARTWORK_CHECK_FEE : 0;

export const getSheetTotalPrice = (
  width,
  height,
  quantity = 1,
  artworkCheck = false,
) =>
  Number(
    (
      getSheetUnitPrice(width, height) * Number(quantity) +
      getArtworkCheckFee(artworkCheck)
    ).toFixed(2),
  );

export const inchesToUiPx = (inches) => Number(inches) * UI_PPI;

export const uiPxToInches = (px) => Number(px) / UI_PPI;

export const getExportMultiplier = () => EXPORT_DPI / UI_PPI;

/** Cap export so browser/Cloudinary can handle tall sheets (e.g. 22×200). */
export const getSafeExportMultiplier = (
  sheetWidth,
  sheetHeight,
  maxPx = 2048,
) => {
  const widthPx = Math.max(1, inchesToUiPx(sheetWidth));
  const heightPx = Math.max(1, inchesToUiPx(sheetHeight));
  const ideal = Math.min(getExportMultiplier(), 8);
  const capped = Math.min(maxPx / widthPx, maxPx / heightPx);
  return Math.max(0.5, Math.min(ideal, capped));
};

export const getDesignGapPx = () => DESIGN_GAP_IN * UI_PPI;

export const estimateDpi = (pixelWidth, printWidthInches) => {
  if (!printWidthInches || printWidthInches <= 0) return 0;
  return Math.round(Number(pixelWidth) / Number(printWidthInches));
};

/** Build designsMeta with layout in inches (x, y, width, height). */
export const buildDesignsMetaFromObjects = (objects = []) =>
  (objects || []).map((obj) => {
    const widthIn = uiPxToInches(
      obj.getScaledWidth?.() ?? obj.width * (obj.scaleX || 1),
    );
    const heightIn = uiPxToInches(
      obj.getScaledHeight?.() ?? obj.height * (obj.scaleY || 1),
    );
    const xIn = uiPxToInches(obj.left || 0);
    const yIn = uiPxToInches(obj.top || 0);
    const src =
      obj.designMeta?.secureUrl ||
      (typeof obj.getSrc === "function" ? obj.getSrc() : obj.src) ||
      null;

    return {
      fileName: obj.designMeta?.fileName || null,
      publicId: obj.designMeta?.publicId || null,
      secureUrl: src,
      x: Number(xIn.toFixed(3)),
      y: Number(yIn.toFixed(3)),
      width: Number(widthIn.toFixed(3)),
      height: Number(heightIn.toFixed(3)),
    };
  });

export const buildGangSheetCartItem = ({
  mode,
  sheetWidth,
  sheetHeight,
  quantity,
  fileName,
  previewUrl,
  secureUrl,
  publicId,
  artworkCheck = false,
  designsMeta = [],
  canvasData = null,
}) => {
  const width = Number(sheetWidth) || SHEET_WIDTH_IN;
  const height = Number(sheetHeight) || 20;
  const qty = Math.max(1, Number(quantity) || 1);
  const unitPrice = getSheetUnitPrice(width, height);
  const artworkCheckFee = getArtworkCheckFee(artworkCheck);
  const sizeTotal = Number((unitPrice * qty).toFixed(2));

  return {
    id: `gang-sheet-${Date.now()}`,
    productType: "gang-sheet",
    mode: mode || "create",
    sheetWidth: width,
    sheetHeight: height,
    fileName: fileName || `Gang Sheet ${width}x${height}`,
    previewUrl,
    secureUrl: secureUrl || previewUrl,
    publicId: publicId || null,
    removeBackground: false,
    cutSeparately: false,
    cuttingUnitPrice: 0,
    cuttingFee: 0,
    artworkCheck: Boolean(artworkCheck),
    artworkCheckFee,
    designsMeta: Array.isArray(designsMeta) ? designsMeta : [],
    canvasData,
    type: "DTF by Gang Sheet",
    sizes: [
      {
        width,
        height,
        quantity: qty,
        unitPrice,
        totalPrice: sizeTotal,
      },
    ],
    total: Number((sizeTotal + artworkCheckFee).toFixed(2)),
  };
};

export const canAddGangSheetToCart = (item) =>
  Boolean(
    (item?.secureUrl || item?.previewUrl) &&
      Number(item?.sheetHeight || item?.sizes?.[0]?.height) >= 20 &&
      Number(item?.sizes?.[0]?.quantity || item?.quantity || 1) >= 1 &&
      Number(item?.total) >= 0,
  );

/** Basic left-to-right, top-to-bottom shelf packing for Fabric image objects. */
export const autoArrangeObjects = (objects, sheetWidthPx, sheetHeightPx, gapPx) => {
  let cursorX = gapPx;
  let cursorY = gapPx;
  let rowHeight = 0;

  objects.forEach((obj) => {
    const width = obj.getScaledWidth();
    const height = obj.getScaledHeight();

    if (cursorX + width > sheetWidthPx - gapPx) {
      cursorX = gapPx;
      cursorY += rowHeight + gapPx;
      rowHeight = 0;
    }

    if (cursorY + height > sheetHeightPx - gapPx) {
      // Keep placing; soft overflow — user can resize sheet
    }

    obj.set({
      left: cursorX,
      top: cursorY,
      originX: "left",
      originY: "top",
    });
    obj.setCoords();

    cursorX += width + gapPx;
    rowHeight = Math.max(rowHeight, height);
  });
};

export const objectsOverlap = (a, b) => {
  const aBound = a.getBoundingRect();
  const bBound = b.getBoundingRect();
  return !(
    aBound.left + aBound.width <= bBound.left ||
    bBound.left + bBound.width <= aBound.left ||
    aBound.top + aBound.height <= bBound.top ||
    bBound.top + bBound.height <= aBound.top
  );
};

export const hasOverlappingDesigns = (objects) => {
  for (let i = 0; i < objects.length; i += 1) {
    for (let j = i + 1; j < objects.length; j += 1) {
      if (objectsOverlap(objects[i], objects[j])) return true;
    }
  }
  return false;
};
